import { ApolloQueryResult } from '@apollo/client';
import { IProps } from '@components/messages/types';
import React, { useEffect, useState } from 'react';
import { ITableActions } from 'apps/things/app/item/context/ItemProvider';
import { useLazyGetAllThingBrokerQuery } from 'apps/events/app/broker/queries';
import { GetAllThingBrokers, GetAllThingBrokersVariables, GetAllThingBrokers_getAllThingBrokers_page_edges } from 'apps/events/app/broker/types/GetAllThingBrokers';
import useOVSPagination from 'hooks/useOVSPagination';
import { funNumberAgr } from '@types';
export interface IThingBrokerContext {
  items: GetAllThingBrokers_getAllThingBrokers_page_edges[];
  setItems: (item: GetAllThingBrokers_getAllThingBrokers_page_edges[]) => void;
  loading: boolean;
  getItems: () => void;
  activeStage: number;
  setActiveStage: (stage: number) => void;
  tableActions: ITableActions[];
  refetchItems:
    | ((
        variables?: Partial<GetAllThingBrokersVariables> | undefined
      ) => Promise<ApolloQueryResult<GetAllThingBrokers>>)
    | undefined;
    setPageLimit: funNumberAgr;
    goTo: (nextPrev: boolean) => void;
    paging: any;
}

export const BrokerContext = React.createContext<IThingBrokerContext>(
  {} as IThingBrokerContext
);

const ThingBrokerProvider: React.FC<IProps> = ({ children }) => {
  const [activeStage, setActiveStage] = useState(0);
  const [item, setItem] = React.useState(
    {} as GetAllThingBrokers_getAllThingBrokers_page_edges
  );
  const [items, setItems] = React.useState<GetAllThingBrokers_getAllThingBrokers_page_edges[]>(
    []
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  /// hooks
  const {
    endCursorStack,
    initPagAction,
    setPaging,
    paging,
    setPageListLimit,
    pageListLimit
  } = useOVSPagination();


  const [
    getItems,
    { refetch: refetchItems, data, loading, fetchMore },
  ] = useLazyGetAllThingBrokerQuery({
    first: pageListLimit,
  });

  useEffect(() => {
    setItems(data?.getAllThingBrokers?.page?.edges || []);
    setPaging({
      ...data?.getAllThingBrokers?.pageData,
      ...data?.getAllThingBrokers?.page?.pageInfo,
    }); // eslint-disable-next-line
  }, [data]);

  const goTo = async (next = true) => {
    if (isLoadingMore) return;

    let variables: GetAllThingBrokersVariables = {
      first: pageListLimit,
      ...initPagAction(next),
    };

    if (fetchMore) {
      setIsLoadingMore(true);
      const _data = await fetchMore({
        variables,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          setIsLoadingMore(false);
          if (!fetchMoreResult) {
            return previousResult;
          }
          return {
            ...fetchMoreResult,
          };
        },
      });

      setItems(_data?.data?.getAllThingBrokers?.page?.edges || []);

      setPaging({
        ..._data?.data?.getAllThingBrokers?.pageData,
        ..._data?.data?.getAllThingBrokers?.page?.pageInfo,
        hasPreviousPage: endCursorStack.length > 0,
      });
    }
  };

  const setPageLimit = (limit: number) => {
    setPageListLimit(limit)
    setTimeout(() => {
      refetchItems && refetchItems()
    }, 100);

  }
  const value = React.useMemo(
    () => ({
      item,
      setItem,
      items,
      setItems,
      loading: loading,
      getItems: getItems,
      refetchItems,
      tableActions: [],
      activeStage,
      setActiveStage,
      setPageLimit,
      goTo,
      paging
    }), // eslint-disable-next-line
    [items, loading]
  );

  return (
    <BrokerContext.Provider value={value}>{children}</BrokerContext.Provider>
  );
};

export default ThingBrokerProvider;
